import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import { Grid } from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { t } from '../../../../../../types/translation/Translator';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { ProductTransactionSpecifiers } from '../../../../../../types/productTransaction';
import { cloneDeep } from '@apollo/client/utilities';
import { TestIdIdentifier } from '../../../../../../util/identifiers/identifiers.util';
import { ProductContext } from '../../../../../../context/ProductContext';
import TextInput from '../../../../Common/TextInput';
import { CustomFieldContext } from '../../../../../../context/CustomFieldContext';
import { OrderInput, OrderUtil } from '../../../../../../types/order/';
import { TagRelationContext } from '../../../../../../context/TagRelationContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import Table, { TableHeader } from '../../../../../components/Table';
import { ProductOrContainerNameColumn } from '../../../../../components/Table/ProductOrContainer';
import PackageIcon from '../../../../../../VentoryUI/icons/Package/PackagIcon';
import DropdownSelect from '../../../../Common/DropdownSelect';
import Button, { DeleteButton } from '../../../../../../VentoryUI/components/common/Button';
import { UserContext } from '../../../../../../context/UserContext';
import { UnitOfMeasure, UnitOfMeasureUtil } from '../../../../../../types/uom';
import { BinContext } from '../../../../../../context/BinContext';
import { BinStatusContext } from '../../../../../../context/BinStatusContext';
import { HandlingUnitContext } from '../../../../../../context/HandlingUnitContext';
import { FlowType } from '../../../../../../types/order/orderFlow';

interface CreateOrderProductPaneProps {
  order: OrderInput;
  setOrder: (order: OrderInput) => void;
  next?: () => void;
  back?: () => void;
  add?: () => void;
  cancel?: () => void;
  loading?: boolean;
  setError: (err: string) => void;
  disabled?: boolean;
}

export default function CreateOrderProductPane({
  order,
  setOrder,
  next,
  back,
  add,
  cancel,
  setError,
  loading = false,
  disabled = false,
}: CreateOrderProductPaneProps) {
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { products, productsLoading } = useContext(ProductContext);
  const { bins } = useContext(BinContext);
  const { binStatuses } = useContext(BinStatusContext);
  const { customFields } = useContext(CustomFieldContext);
  const { tagRelations } = useContext(TagRelationContext);
  const { containers } = useContext(ContainerContext);
  const { currentCompany } = useContext(CompanyContext);
  const { handlingUnits } = useContext(HandlingUnitContext);
  const { locale } = useContext(UserContext);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [customFieldDatePickerOpen, setCustomFieldDatePickerOpen] = useState(false);

  const orderContainerIds = useMemo(() => {
    return new Set([...order.products.values()].filter(p => p.containerId).map(p => p.containerId!));
  }, []);

  const orderProducts = useMemo(() => {
    return [...order.products.values()].filter(p => !p.toContainerId);
  }, [order]);

  const headers: TableHeader<ProductTransactionSpecifiers>[] = [
    {
      key: 'productNumber',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        return (
          <ProductOrContainerNameColumn
            productMasterDataId={item.pmdId}
            containerId={item.containerId}
            productMasterData={productMasterData}
            containers={containers}
          />
        );
      },
    },

    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: ProductTransactionSpecifiers) => {
        if (item.containerId) return <></>;
        return (
          <Grid container className='h-7' justifyContent={'flex-end'}>
            <Grid item xs={6} marginY={'auto'} justifyContent={'flex-end'} mr={0.1}>
              <TextInput
                disabled={!!item.containerId}
                onChange={v => {
                  const foundItem = order.products.get(OrderUtil.orderSpecifierHash(item) || '');
                  if (!foundItem) return;
                  foundItem.quantity = parseFloat(v) || 0;
                  setOrder(cloneDeep(order));
                }}
                value={item.quantity.toString()}
                dynamicUpdate
                testId={new TestIdIdentifier(`quantityInput${item.pmdId}`)}
                heightClass='h-7'
                type='number'
                // Making the suffix a space by default so the text input will display the suffix version even if the UnitOfMeasure is quantity.
                suffix={UnitOfMeasureUtil.getSuffix(productMasterData.get(item.pmdId || '')?.unitOfMeasure) || ' '}
              />
            </Grid>
          </Grid>
        );
      },
      sortValue: (item: ProductTransactionSpecifiers) => item.quantity,
    },
  ].filter(e => e != null) as TableHeader<ProductTransactionSpecifiers>[];

  const quantityForProduct = new Map<string, { quantity: number; quantityByHandlingUnit: number }>();

  for (const key of [...products.keys()]) {
    const quantity = [...(products.get(key)?.values() || [])]
      .filter(item => {
        if (item.companyId !== order.companyId) return false;
        const outboundAllowed = binStatuses.get(bins.get(item.binId || '')?.binStatusId || '')?.outboundAllowed ?? true;
        if (order.type === FlowType.disposal && outboundAllowed) return false;
        else if (order.type !== FlowType.disposal && !outboundAllowed) return false;

        if (item.containerId && orderContainerIds.has(item.containerId)) return false;

        if (order.fromStockLocationId?.includes('tag::')) {
          const tagId = order.fromStockLocationId?.replace('tag::', '');
          const relations = new Set(
            [...tagRelations.values()].filter(rel => rel.tagId === tagId).map(rel => rel.entityId),
          );
          return relations.has(item.stockLocationId);
        }
        return item.stockLocationId === order.fromStockLocationId;
      })
      .reduce((sum, p) => sum + BigInt(p.unitQuantity), BigInt(0));

    if (quantity !== BigInt(0)) {
      const displayQuantity = UnitOfMeasure.toQuantity(productMasterData.get(key)?.unitOfMeasure, quantity.toString());
      quantityForProduct.set(key, { quantity: parseFloat(displayQuantity), quantityByHandlingUnit: 0 });

      const hUnits = Array.from(handlingUnits.values()).filter(hu => hu.parentId === key);
      if (hUnits) {
        for (const hu of hUnits) {
          const existingQuantity = quantityForProduct.get(hu.productMasterDataId);

          const huQuantity = parseFloat(displayQuantity) * hu.quantity;
          const displayHuQuantity = parseFloat(
            UnitOfMeasure.toQuantity(
              productMasterData.get(hu.productMasterDataId)?.unitOfMeasure,
              huQuantity.toString(),
            ),
          );
          quantityForProduct.set(hu.productMasterDataId, {
            quantity: existingQuantity?.quantity || 0,
            quantityByHandlingUnit: (existingQuantity?.quantityByHandlingUnit || 0) + displayHuQuantity,
          });
        }
      }
    }
  }

  return (
    <div className='mx-6 flex h-full flex-col gap-2 py-2'>
      <DropdownSelect
        disabled={productMasterDataLoading || productsLoading || loading || disabled}
        selectOnly
        values={[...productMasterData.values()].filter(
          pmd =>
            pmd.companyId === order.companyId &&
            !order.products.has(OrderUtil.orderSpecifierHashByValue(pmd.id, undefined)) &&
            (order.type === FlowType.inbound ||
              quantityForProduct.get(pmd.id) ||
              currentCompany.settings.featureToggles.orders.ignoreAvailableQuantity),
        )}
        selectedValue={null}
        toText={item => `${item?.productName} - ${item.productNumber}` || ''}
        toElement={item => {
          const quantity = `${quantityForProduct.get(item.id)?.quantity || 0} ${UnitOfMeasureUtil.getSuffix(
            item.unitOfMeasure,
          )}`;
          const handlingUnitQuantity = quantityForProduct.get(item.id)?.quantityByHandlingUnit;
          return (
            <div className='my-1 flex gap-3'>
              <div className='flex justify-center'>
                <PackageIcon />
              </div>
              <div className=''>
                <p className='text-sm font-bold'>{item.productName}</p>
                <p className='text-sm text-gray-500'>{item.productNumber}</p>
                {OrderUtil.hasFromStockLocation(order) ? (
                  <p className={`text-sm text-gray-400`}>{`Available Quantity: ${quantity} ${
                    handlingUnitQuantity ? `(${handlingUnitQuantity})` : ''
                  }`}</p>
                ) : null}
              </div>
            </div>
          );
        }}
        onChange={item => {
          if (!item) return;
          setOrder(order.withProduct(item.id));
        }}
        placeholder={t(TranslationKey.selectProduct).singular.upper}
      />
      <Table<ProductTransactionSpecifiers>
        modal
        uniqueIdentifier={item => OrderUtil.orderSpecifierHash(item)}
        loading={productMasterDataLoading || productsLoading}
        onSelected={items => setSelected(new Set(items))}
        selectedValues={selected}
        items={orderProducts}
        headers={headers}
      />
      <div className='flex justify-between'>
        <div className='flex gap-2'>
          {selected.size ? (
            <DeleteButton
              loading={loading}
              onClick={() => {
                setError('');
                setOrder(order.removeProducts(selected));
                setSelected(new Set());
              }}
            />
          ) : null}
        </div>
        <div className='flex gap-2'>
          {back ? <Button onClick={back} text={t(TranslationKey.back).singular.label} disabled={loading} /> : null}
          {next ? (
            <Button
              style='secondary'
              loading={loading}
              disabled={!order.products.size}
              onClick={next}
              text={
                !order.fromStockLocationId?.includes('tag::')
                  ? t(TranslationKey.next).singular.label
                  : t(TranslationKey.done).singular.label
              }
            />
          ) : null}
          {cancel ? (
            <Button
              disabled={!order.products.size || loading}
              onClick={cancel}
              text={t(TranslationKey.cancel).singular.label}
            />
          ) : null}
          {add ? (
            <Button
              style='secondary'
              loading={loading}
              disabled={!order.products.size}
              onClick={add}
              text={t(TranslationKey.add).singular.label}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
